import { useMemo } from "react";
import { useTranslation } from "react-i18next";

type PageItem = { type: "page"; value: number } | { type: "ellipsis"; key: string };

export const buildPageList = (current: number, total: number): PageItem[] => {
  if (total <= 7) {
    return Array.from({ length: total }, (_, index) => ({ type: "page", value: index + 1 }));
  }

  const pages = new Set<number>();
  pages.add(1);
  pages.add(total);
  for (let offset = -1; offset <= 1; offset += 1) {
    const page = current + offset;
    if (page > 1 && page < total) {
      pages.add(page);
    }
  }

  const ordered = Array.from(pages).sort((a, b) => a - b);
  const result: PageItem[] = [];
  let last = 0;
  for (const page of ordered) {
    if (page - last > 1) {
      result.push({ type: "ellipsis", key: `ellipsis-${last}-${page}` });
    }
    result.push({ type: "page", value: page });
    last = page;
  }

  return result;
};

export type PaginationProps = {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  pageSizeOptions?: number[];
};

export default function Pagination({
  currentPage,
  totalPages,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 30, 50, 100],
}: PaginationProps) {
  const { t } = useTranslation("common");

  const safePage = Math.min(Math.max(1, currentPage), Math.max(1, totalPages));
  const pageList = useMemo(() => buildPageList(safePage, totalPages), [safePage, totalPages]);

  if (totalPages <= 1 && pageSizeOptions.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 p-4">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs text-gray-500">
          {t("adminPageStatus", { current: safePage, total: totalPages })}
        </span>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">{t("adminRowsPerPage")}</span>
          <select
            value={pageSize}
            onChange={(event) => onPageSizeChange(Number(event.target.value))}
            className="px-2 py-1 rounded-md border border-gray-200 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-primary-300"
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
      </div>
      
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onPageChange(Math.max(1, safePage - 1))}
          disabled={safePage === 1}
          className="px-2 py-1 text-xs rounded-md bg-gray-100 text-gray-600 disabled:opacity-50 hover:bg-gray-200 transition-colors"
        >
          {t("adminPrevPage")}
        </button>
        {pageList.map((item) =>
          item.type === "ellipsis" ? (
            <span key={item.key} className="px-2 text-xs text-gray-400">
              …
            </span>
          ) : (
            <button
              key={item.value}
              type="button"
              onClick={() => onPageChange(item.value)}
              className={`px-2 py-1 text-xs rounded-md transition-colors ${
                item.value === safePage
                  ? "bg-primary-400 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {item.value}
            </button>
          )
        )}
        <button
          type="button"
          onClick={() => onPageChange(Math.min(totalPages, safePage + 1))}
          disabled={safePage === totalPages}
          className="px-2 py-1 text-xs rounded-md bg-gray-100 text-gray-600 disabled:opacity-50 hover:bg-gray-200 transition-colors"
        >
          {t("adminNextPage")}
        </button>
      </div>
    </div>
  );
}
